import { AppDispatch } from '../store';
import { loginSuccess, loginFailure, logout, refreshSuccess, userInfoSuccess, roleConfigSuccess } from '../reducers/authReducer';
import { userAccountAjax } from '../../ajax/UserAccountAjax';
import { settingsAjax } from '../../ajax/SettingsAjax';
import { isSuccessfulLoginResponse } from '../../types/Auth/SuccessfulLoginResponse';
import { isRefreshResponse } from "../../types/Auth/RefreshResponse";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { isOnlineNow } from "../../offline/connectivity";

export const loginUser = (email: string, password: string) => async (dispatch: AppDispatch) => {
  try {
    const response = await userAccountAjax.login(email, password);

    if (isSuccessfulLoginResponse(response)) {
      dispatch(loginSuccess(response));

      const userDetails = await userAccountAjax.getMyInfo();
      if (!userDetails || userDetails instanceof ErrorResponse) {
        dispatch(loginFailure('Failed to retrieve user details.'));
        return false;
      }
      dispatch(userInfoSuccess(userDetails));

      const roleConfig = await settingsAjax.getRoleConfig();
      if (!roleConfig || roleConfig instanceof ErrorResponse) {
        dispatch(loginFailure('Failed to retrieve role configuration.'));
        return false;
      }
      dispatch(roleConfigSuccess(roleConfig));

      return true;
    } else {
      dispatch(loginFailure('Login failed. Please check your credentials and try again.'));
      return false;
    }
  } catch {
    dispatch(loginFailure('Login failed. Please try again.'));
    return false;
  }
};

export const logoutUser = () => async (dispatch: AppDispatch) => {
  dispatch(logout())
};

export const refreshToken = (refreshToken: string) => async (dispatch: AppDispatch) => {
  // Note: only sign out when the server actually rejects the refresh token. A refresh that cannot
  // reach the server - offline, or the API down - must leave the session alone: logging out strands
  // the crew, because they cannot log back in without a connection and every queued offline edit is
  // behind that login. The access token lasts an hour, so an outage longer than that used to log the
  // user out mid-shift. Requests carry on with the expired token (they fail offline anyway) and the
  // 401 handler refreshes properly on reconnect.
  // Note: navigator.onLine is not the check to make here - it stays true when the device has no route
  // to the server, which is exactly the case this guard exists for. isOnlineNow also weighs whether
  // the last request actually reached anything.
  if (!isOnlineNow()) return null;

  try {
    const response = await userAccountAjax.refreshToken(refreshToken);
    if (isRefreshResponse(response)) {
      dispatch(refreshSuccess(response));
      return response;
    }

    // Note: sign out only when the server itself rejected the token. Any other failure shape - a 502
    // from a proxy, an HTML error page, a request that got no further than the browser - is a
    // connectivity problem wearing a response's clothing, and treating it as a rejection strands the
    // crew: they cannot log back in without a connection, and every queued offline edit sits behind
    // that login.
    const rejectedByServer = Array.isArray(response?.errors)
      && response.errors.some(e => e.code === 400 || e.code === 401 || e.code === 403);

    if (rejectedByServer) {
      console.error('Refresh token rejected by the server; signing out', response);
      dispatch(logout());
    } else {
      console.error('Token refresh failed without a rejection; keeping the session', response);
    }
    return null;
  } catch {
    return null;
  }
}